/**
 * Centralized environment / URL helpers.
 * Env-specific hosts/API: REACT_APP_* + env.defaults.js (shared with server CSP).
 * External product links: plain constants below (not env-driven).
 */

// Shared with Node CSP (CommonJS). CRA/webpack can require .js from TS.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const envDefaults = require("../config/env.defaults.js");

type EnvBucket = {
  siteUrl: string;
  appUrl: string;
  apiUrl: string;
  appOrigins: string[];
};

function trimTrailingSlash(url: string): string {
  return url.replace(/\/$/, "");
}

/**
 * Resolve env bucket. Prefer explicit `APP_ENV`.
 * CRA does not embed non-REACT_APP_* vars in the browser bundle, so the client
 * falls through to REACT_APP_API_URL inference when APP_ENV is unset at build time.
 */
function resolveAppEnv(): "production" | "development" | "staging" {
  const raw = (process.env.APP_ENV || "").toLowerCase();

  if (raw === "development" || raw === "dev") return "development";
  if (raw === "staging" || raw === "stage") return "staging";
  if (raw === "production" || raw === "prod") return "production";

  if (process.env.NODE_ENV === "development") return "development";

  const api = (process.env.REACT_APP_API_URL || "").toLowerCase();
  if (api.includes("appening")) return "development";
  if (api.includes("vlemobile.com")) return "production";

  // Historical DEV fallback: marketing host vle-next.appening.xyz
  return "development";
}

function envBucket(): EnvBucket {
  const key = resolveAppEnv();
  return (envDefaults[key] || envDefaults.development) as EnvBucket;
}

export function isLocalDevHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

/** Marketing / public site (e.g. vlemobile.com / vle-next.appening.xyz). */
function resolveSiteBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_SITE_URL?.trim();
  if (fromEnv) return trimTrailingSlash(fromEnv);

  if (isLocalDevHost()) {
    return window.location.origin;
  }

  return envBucket().siteUrl;
}

/**
 * This SPA's public origin (e.g. app.vlemobile.com / vle.appening.xyz).
 * Use for absolute links to in-app routes when relative paths are not appropriate.
 */
function resolveAppBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_APP_URL?.trim();
  if (fromEnv) return trimTrailingSlash(fromEnv);

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return envBucket().appUrl;
}

/** Backend API base URL (no trailing slash). */
export function resolveApiBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL?.trim();
  if (fromEnv) return trimTrailingSlash(fromEnv);
  return envBucket().apiUrl;
}

export const API_BASE_URL = resolveApiBaseUrl();
export const APP_ENV = resolveAppEnv();

export const createAbsoluteUrl = (path: string = "") => {
  const baseUrl = resolveSiteBaseUrl();
  return path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;
};

/** Absolute URL on the app host (this React SPA). */
export const createAppAbsoluteUrl = (path: string = "") => {
  const baseUrl = resolveAppBaseUrl();
  return path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;
};

/** Host + path label for legal/FAQ display (environment-aware). */
export function formatAppHostLabel(path: string = ""): string {
  try {
    const url = new URL(createAppAbsoluteUrl(path));
    const suffix = `${url.pathname}${url.hash}`.replace(/\/$/, "");
    return suffix && suffix !== "/" ? `${url.host}${suffix}` : url.host;
  } catch {
    return path.replace(/^\//, "");
  }
}

/** Marketing-site host label (environment-aware). */
export function formatSiteHostLabel(path: string = ""): string {
  try {
    const url = new URL(createAbsoluteUrl(path));
    const suffix = `${url.pathname}${url.hash}`.replace(/\/$/, "");
    return suffix && suffix !== "/" ? `${url.host}${suffix}` : url.host;
  } catch {
    return path.replace(/^\//, "");
  }
}

const HELP_CENTER_URL = "https://vlemobile.tawk.help";

/** Product / third-party external links. */
export const externalUrls = {
  cdnBase: "https://vlecdn.b-cdn.net",
  unlockingGuide: "https://vlecdn.b-cdn.net/unlocking-guide.html",
  portingGuide: "https://downloadz.b-cdn.net/porting-guide.html",
  helpCenter: HELP_CENTER_URL,
  helpCenterPath: (path: string = "") => {
    if (!path) return HELP_CENTER_URL;
    return `${HELP_CENTER_URL}/${path.replace(/^\//, "")}`;
  },
  parentsGuide:
    "https://ww2.vlemobile.com/protect-guide-your-kids-with-vle-mobile",
  scheduleCallEmbed:
    "https://meet.brevo.com/vle-mobile/borderless?l=introduction-phone-call",
  newsletterForm:
    "https://69d5cafd.sibforms.com/serve/MUIFAO4prUaOWNci7DltcMpo52Ud17yq-w6ioXwQSWlRCOZ4QCRsq1hk6g9zJe-MB64Na6obl2oXBGdwZY1D4jkCHSVxFK8UnTSJabkA-imv8eh26VgmVuijyB9cjXUlvH4EZ2GnrKD7w8d8lvdkKe7l4sq9zMrz-u-cR4i2QTRzDWdsLyANva224gUDF1q0Bqx-O2OS0GnqSORM",
  agentResources:
    "https://appeningxyz-my.sharepoint.com/:f:/g/personal/nikunj_s_appening_xyz/EqneHGUQG7dIqle7AYLmDzgB1LtXD0phpyf4ouGFkejiMg?e=OzftmK",
};
